import React, { useEffect, useState, createContext, useContext, ReactNode } from 'react';
export interface CurrencyRate {
  symbol: string;
  code: string;
  rate: number;
}
interface CurrencyContextType {
  currency: CurrencyRate;
  loading: boolean;
  formatPrice: (usdPrice: number) => string;
}
const RATE_MAP: Record<string, CurrencyRate> = {
  US: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  MX: {
    symbol: '$',
    code: 'MXN',
    rate: 19.5
  },
  CO: {
    symbol: '$',
    code: 'COP',
    rate: 4000
  },
  AR: {
    symbol: '$',
    code: 'ARS',
    rate: 1000
  },
  CL: {
    symbol: '$',
    code: 'CLP',
    rate: 950
  },
  PE: {
    symbol: 'S/',
    code: 'PEN',
    rate: 3.7
  },
  EC: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  UY: {
    symbol: '$',
    code: 'UYU',
    rate: 40
  },
  BR: {
    symbol: 'R$',
    code: 'BRL',
    rate: 5.5
  },
  ES: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  DE: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  FR: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  IT: {
    symbol: '€',
    code: 'EUR',
    rate: 0.92
  },
  GB: {
    symbol: '£',
    code: 'GBP',
    rate: 0.78
  },
  CR: {
    symbol: '₡',
    code: 'CRC',
    rate: 500
  },
  DO: {
    symbol: 'RD$',
    code: 'DOP',
    rate: 58
  },
  GT: {
    symbol: 'Q',
    code: 'GTQ',
    rate: 7.8
  },
  HN: {
    symbol: 'L',
    code: 'HNL',
    rate: 24.7
  },
  PA: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  PY: {
    symbol: '₲',
    code: 'PYG',
    rate: 7500
  },
  BO: {
    symbol: 'Bs',
    code: 'BOB',
    rate: 6.9
  },
  VE: {
    symbol: '$',
    code: 'USD',
    rate: 1
  },
  NI: {
    symbol: 'C$',
    code: 'NIO',
    rate: 36.6
  },
  SV: {
    symbol: '$',
    code: 'USD',
    rate: 1
  }
};
const DEFAULT_CURRENCY: CurrencyRate = {
  symbol: '$',
  code: 'USD',
  rate: 1
};
const CACHE_KEY = 'detected_currency';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyRate>(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const {
            data,
            timestamp
          } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setCurrency(data);
            setLoading(false);
            return;
          }
        }
        // Fetch if no valid cache
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(4000)
        });
        const data = await res.json();
        const countryCode = data?.country_code || 'US';
        const detectedRate = RATE_MAP[countryCode] || DEFAULT_CURRENCY;
        setCurrency(detectedRate);
        // Save to cache
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: detectedRate,
          timestamp: Date.now()
        }));
      } catch {
        setCurrency(DEFAULT_CURRENCY);
      } finally {
        setLoading(false);
      }
    };
    detectCurrency();
  }, []);
  const formatPrice = (usdPrice: number) => {
    if (loading) return '...';
    const converted = usdPrice * currency.rate;
    // Format without decimals for large numbers (like COP, PYG)
    if (currency.rate > 100) {
      return `${currency.symbol}${Math.round(converted).toLocaleString('es-ES')}`;
    }
    // For smaller rates, show 2 decimals
    return `${currency.symbol}${converted.toFixed(2)}`;
  };
  return <CurrencyContext.Provider value={{
    currency,
    loading,
    formatPrice
  }} data-id="element-142">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}